import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';

import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { configureStore } from './store';

import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MsalProvider } from '@azure/msal-react';
import { msalInstanceByAdib } from './authConfigAdib';
import { msalInstanceByThriwe } from './authConfigThriwe';
import { applyDomainQueryParamsToLocation } from './Routes/domainConfig';
import patchReactFlatpickr from './helpers/patchReactFlatpickr';

patchReactFlatpickr();

const environment = process.env.REACT_APP_NODE_ENV;
const clientDSN = process.env.REACT_APP_SENTRY_DSN;

const Main = () => {
  const [msalInstance, setMsalInstance] = useState(msalInstanceByAdib);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const value = queryParams.get('auth-source');
    if (value === 'thriwe') {
      setMsalInstance(msalInstanceByThriwe);
    } else {
      setMsalInstance(msalInstanceByAdib);
    }
  }, []);

  return (
    <Provider store={configureStore({})}>
      <BrowserRouter basename={process.env.PUBLIC_URL}>
        <ToastContainer />
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_OAUTH}>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </GoogleOAuthProvider>
      </BrowserRouter>
    </Provider>
  );
};

/*
 * MSAL popup landing guard.
 *
 * Every Microsoft sign-in flow here sends the popup back to a URL inside this app
 * (`/login` for <MicrosoftLogin />, `/` for the adib msal instance) with the auth
 * response in the hash. Booting the SPA inside that popup makes it consume the
 * response — MicrosoftLogin's own handleRedirectPromise fires, or the router
 * redirects — which clears the hash the opener is polling for, so loginPopup()
 * never resolves and the popup is never closed. Only shows up in fullscreen,
 * where the browser turns the popup into a real tab and throttles the opener's
 * 30ms poll to ~1s, losing the race.
 *
 * The opener reads the hash straight off this document's URL, so the popup just
 * has to sit still and not navigate. The opener/window.name conditions mirror
 * msal-browser's own isInPopup() (identical in the v2 bundled by
 * react-microsoft-login and in v3) so the redirect (non-popup) flow still boots
 * the app normally and handleRedirectPromise can run there.
 */
const AUTH_RESPONSE_HASH = /[#&](code|error|error_description|state|id_token|access_token)=/;

const isAuthPopupLanding = () => {
  try {
    return (
      !!window.opener &&
      window.opener !== window &&
      typeof window.name === 'string' &&
      window.name.indexOf('msal.') === 0 &&
      AUTH_RESPONSE_HASH.test(window.location.hash)
    );
  } catch (e) {
    return false;
  }
};

// On edge.thriwe.com every URL carries `userVersion=v2`. Correcting the address bar before the
// first render means a refresh, a bookmark or a pasted link starts out with it, so the router and
// the very first API call already see it. No-op on the other domains.
applyDomainQueryParamsToLocation();

const root = createRoot(document.getElementById('root'));

if (isAuthPopupLanding()) {
  root.render(<div style={{ font: '15px sans-serif', padding: '24px', textAlign: 'center' }}>Signing you in…</div>);
} else {
  root.render(<Main />);
  reportWebVitals();
}
